import { TFunction } from "i18next";
import { CircleCheck, IdCard, Lock } from "lucide-react";
import {
  ListItemBadge,
  ListItemCardMetrics,
  ListItemCardProps,
} from "@components/ListItemCardCst/types";

interface MapUserOptions {
  tInput: TFunction;
  corporateId?: string;
}

const readCount = (...candidates: unknown[]): number | undefined => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate.length;
    if (typeof candidate === "number" && !Number.isNaN(candidate))
      return candidate;
  }
  return undefined;
};

const translateRoles = (
  user: any,
  tInput: TFunction,
  corporateId?: string,
): string => {
  if (!Array.isArray(user?.roles)) return "";
  const optionsKey = corporateId === "1" ? "roles-digited" : "roles";
  return user.roles
    .map((role: any) => {
      const value =
        typeof role === "string" ? role : (role?.label ?? role?.value);
      if (!value) return "";
      return tInput(`options.${optionsKey}.${String(value).toLowerCase()}`, {
        defaultValue: value,
      });
    })
    .filter(Boolean)
    .join(", ");
};

const buildBadges = (user: any, tInput: TFunction): ListItemBadge[] => {
  const status = String(user.status || "").toLowerCase();
  if (!status) return [];
  const isSuspended = status === "suspended";
  return [
    {
      label: String(
        tInput(`options.status_user.${status}`, { defaultValue: user.status }),
      ),
      variant: isSuspended ? "error" : status === "active" ? "success" : "info",
      icon: isSuspended ? <Lock /> : <CircleCheck />,
    },
  ];
};

const buildMetrics = (user: any): ListItemCardMetrics => {
  const counters = user.metadata_counters ?? user.counters ?? {};
  const metrics: ListItemCardMetrics = {};

  const userInitiatives = readCount(
    user.initiatives,
    counters.initiatives,
    user.initiatives_count,
  );
  if (userInitiatives !== undefined) metrics.userInitiatives = userInitiatives;

  const dynamicGroups = readCount(
    user.dynamic_groups,
    counters.dynamic_groups,
    user.dynamic_groups_count,
  );
  if (dynamicGroups !== undefined) metrics.dynamicGroups = dynamicGroups;

  const staticGroups = readCount(
    user.static_groups,
    counters.static_groups,
    user.static_groups_count,
  );
  if (staticGroups !== undefined) metrics.staticGroups = staticGroups;

  const learningObjects = readCount(
    user.learning_objects,
    counters.learning_objects,
    user.learning_objects_count,
  );
  if (learningObjects !== undefined) metrics.learningObjects = learningObjects;

  return metrics;
};

export const mapUserToCard = (
  user: any,
  options: MapUserOptions,
): ListItemCardProps => {
  const { tInput, corporateId } = options;
  const fullName = [user.name, user.surname].filter(Boolean).join(" ").trim();
  const title = fullName || user.email || user.external_id || "-";
  const roles = translateRoles(user, tInput, corporateId);

  return {
    title,
    subtitle: {
      id: user.id_user,
      activityType: user.email || user.external_id || undefined,
      segments: roles ? [{ label: roles, icon: <IdCard /> }] : undefined,
    },
    metrics: buildMetrics(user),
    badges: buildBadges(user, tInput),
    actions: [],
  };
};
